import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import { MdCampaign, MdSend, MdMarkEmailRead, MdShoppingBag, MdCurrencyRupee, MdPercent, MdLocalOffer, MdTrendingUp } from 'react-icons/md';
import TabbedReport, { pill, TwoLine, Dash, pctChip } from './TabbedReport';
import { inr, fmtDate } from './reportShared';

const CHANNEL = { Email: ['#e0e7ff', '#3730a3'], SMS: ['#e0f2fe', '#075985'], WhatsApp: ['#dcfce7', '#166534'] };
const channelChip = (c) => {
  const [bg, fg] = CHANNEL[c] || ['#f1f5f9', '#475569'];
  return <Chip size="small" label={c} sx={pill(bg, fg)} />;
};
const STATUS = { completed: ['#dcfce7', '#166534'], running: ['#e0f2fe', '#075985'], scheduled: ['#ede9fe', '#5b21b6'], pending: ['#fef3c7', '#92400e'], cancelled: ['#f1f5f9', '#64748b'], active: ['#dcfce7', '#166534'], inactive: ['#f1f5f9', '#64748b'], expired: ['#fee2e2', '#991b1b'] };
const statusChip = (s) => {
  const [bg, fg] = STATUS[s] || ['#f1f5f9', '#475569'];
  return <Chip size="small" label={s ? s.charAt(0).toUpperCase() + s.slice(1) : '-'} sx={pill(bg, fg)} />;
};
const money = (key, label, bold) => ({ key, label, numeric: true, bold, text: (r) => r[key], cell: (r) => inr(r[key]), print: (r) => inr(r[key]) });
const pctCol = (key, label, opts) => ({ key, label, center: true, text: (r) => (r[key] === null || r[key] === undefined ? '' : `${r[key]}%`), cell: (r) => pctChip(r[key], opts) });

const tabs = [
  {
    key: 'campaigns',
    label: 'Campaigns',
    defaultSort: 'date_desc',
    sorts: [['date_desc', 'Newest first'], ['revenue_desc', 'Attributed revenue: high to low'], ['conversion_desc', 'Conversion: high to low'], ['sent_desc', 'Most sent']],
    columns: [
      { key: 'name', label: 'Campaign', text: (r) => r.name, cell: (r) => <TwoLine top={r.name} bottom={r.campId} />, extra: (r) => ({ 'Campaign ID': r.campId }) },
      { key: 'channel', label: 'Channel', text: (r) => r.channel, cell: (r) => channelChip(r.channel) },
      { key: 'runAt', label: 'Sent On', text: (r) => fmtDate(r.runAt) },
      { key: 'status', label: 'Status', text: (r) => r.status, cell: (r) => statusChip(r.status) },
      { key: 'audience', label: 'Audience', numeric: true, text: (r) => r.audience },
      { key: 'sent', label: 'Sent', numeric: true, text: (r) => r.sent },
      { key: 'failed', label: 'Failed', numeric: true, text: (r) => r.failed, cell: (r) => (r.failed ? <Chip size="small" label={r.failed} sx={pill('#fee2e2', '#b91c1c')} /> : 0) },
      pctCol('deliveryPct', 'Send Success', { good: 95, warn: 85 }),
      {
        key: 'read',
        label: 'Delivered / Read',
        center: true,
        text: (r) => (r.delivered === null ? '' : `${r.delivered} / ${r.read}`),
        cell: (r) => (r.delivered === null ? <Tooltip title="Only WhatsApp returns delivery / read receipts" arrow><span><Dash /></span></Tooltip> : `${r.delivered} / ${r.read}${r.readPct !== null ? ` (${r.readPct}%)` : ''}`),
      },
      { key: 'orders', label: 'Orders', numeric: true, bold: true, text: (r) => r.orders },
      money('revenue', 'Attributed Revenue', true),
      {
        key: 'conversionPct',
        label: 'Conversion',
        center: true,
        text: (r) => (r.conversionPct === null ? '' : `${r.conversionPct}%`),
        cell: (r) => (r.conversionPct === null ? <Tooltip title="No registered customers targeted — can't track orders" arrow><span><Dash /></span></Tooltip> : pctChip(r.conversionPct, { good: 5, warn: 2 })),
      },
    ],
  },
  {
    key: 'channels',
    label: 'By Channel',
    columns: [
      { key: 'channel', label: 'Channel', text: (r) => r.channel, cell: (r) => channelChip(r.channel) },
      { key: 'campaigns', label: 'Campaigns', numeric: true, text: (r) => r.campaigns },
      { key: 'audience', label: 'Audience', numeric: true, text: (r) => r.audience },
      { key: 'sent', label: 'Sent', numeric: true, text: (r) => r.sent },
      { key: 'failed', label: 'Failed', numeric: true, text: (r) => r.failed },
      pctCol('deliveryPct', 'Send Success', { good: 95, warn: 85 }),
      { key: 'orders', label: 'Orders', numeric: true, bold: true, text: (r) => r.orders },
      money('revenue', 'Attributed Revenue', true),
      pctCol('conversionPct', 'Conversion', { good: 5, warn: 2 }),
    ],
  },
  {
    key: 'coupons',
    label: 'Coupons',
    defaultSort: 'uses_desc',
    sorts: [['uses_desc', 'Most used'], ['revenue_desc', 'Revenue: high to low'], ['discount_desc', 'Discount given: high to low'], ['roi_asc', 'Lowest ROI first']],
    rowAlert: (r) => r.roi !== null && r.roi < 3,
    columns: [
      { key: 'code', label: 'Coupon', bold: true, text: (r) => r.code, cell: (r) => <TwoLine top={r.code} bottom={`${r.offer}${r.minOrder ? ` · min ₹${r.minOrder}` : ''}`} />, extra: (r) => ({ Offer: r.offer, 'Min Order': r.minOrder, 'Valid Till': r.validTill }) },
      { key: 'status', label: 'Status', text: (r) => r.status, cell: (r) => statusChip(r.status) },
      { key: 'uses', label: 'Uses', numeric: true, bold: true, text: (r) => r.uses },
      { key: 'customers', label: 'Customers', numeric: true, text: (r) => r.customers },
      { key: 'limit', label: 'Usage Limit', center: true, text: (r) => (r.usageLimit ? `${r.uses}/${r.usageLimit}` : 'No limit'), cell: (r) => (r.usageLimit ? `${r.uses} / ${r.usageLimit} (${r.limitUsedPct}%)` : <Dash />) },
      money('discount', 'Discount Given'),
      money('revenue', 'Order Revenue', true),
      money('avgOrder', 'Avg Order'),
      {
        key: 'roi',
        label: 'ROI',
        center: true,
        text: (r) => (r.roi === null ? '' : `${r.roi}x`),
        cell: (r) => (r.roi === null ? <Dash /> : <Chip size="small" label={`${r.roi}×`} sx={r.roi >= 8 ? pill('#dcfce7', '#166534') : r.roi >= 3 ? pill('#fef3c7', '#92400e') : pill('#fee2e2', '#b91c1c')} />),
      },
      { key: 'refunded', label: 'Refunded', numeric: true, text: (r) => r.refunded },
    ],
  },
];

const CampaignReport = () => (
  <TabbedReport
    route="reports/campaign-report"
    title="Campaign Report"
    subtitle="Email, SMS and WhatsApp reach, the orders that followed, and how coupons are paying off"
    filePrefix="Campaigns"
    tabs={tabs}
    searchLabel="Search campaign / coupon"
    filters={[
      { key: 'channel', label: 'Channel', tabs: ['campaigns', 'channels'], options: ['Email', 'SMS', 'WhatsApp'].map((c) => ({ value: c, label: c })) },
      { key: 'window', label: 'Attribution window', tabs: ['campaigns', 'channels'], defaultValue: '7', allLabel: null, options: ['1', '3', '7', '14', '30'].map((d) => ({ value: d, label: `${d} day${d === '1' ? '' : 's'} after sending` })) },
    ]}
    cards={(s, { goTo, isActive }) => [
      { label: 'Campaigns', value: (s.campaigns || 0).toLocaleString('en-IN'), icon: MdCampaign, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', sub: 'Email, SMS & WhatsApp', onClick: () => goTo('campaigns'), active: isActive('campaigns') },
      { label: 'Messages Sent', value: (s.sent || 0).toLocaleString('en-IN'), icon: MdSend, gradient: 'linear-gradient(135deg,#0ea5e9,#2563eb)', sub: `${s.failed || 0} failed` },
      { label: 'Send Success', value: s.deliveryPct === null || s.deliveryPct === undefined ? '—' : `${s.deliveryPct}%`, icon: MdMarkEmailRead, gradient: 'linear-gradient(135deg,#14b8a6,#0d9488)', sub: 'Sent ÷ (sent + failed)', onClick: () => goTo('channels'), active: isActive('channels') },
      { label: 'Orders After Campaigns', value: (s.orders || 0).toLocaleString('en-IN'), icon: MdShoppingBag, gradient: 'linear-gradient(135deg,#10b981,#059669)', sub: `Within ${s.windowDays || 7} days`, onClick: () => goTo('campaigns', { sortBy: 'revenue_desc' }), active: isActive('campaigns', { sortBy: 'revenue_desc' }) },
      { label: 'Attributed Revenue', value: inr(s.revenue), icon: MdCurrencyRupee, gradient: 'linear-gradient(135deg,#f59e0b,#ea580c)', sub: 'From targeted customers' },
      { label: 'Conversion', value: s.conversionPct === null || s.conversionPct === undefined ? '—' : `${s.conversionPct}%`, icon: MdPercent, gradient: 'linear-gradient(135deg,#ec4899,#db2777)', sub: 'Targeted customers who ordered' },
      { label: 'Coupon Uses', value: (s.couponUses || 0).toLocaleString('en-IN'), icon: MdLocalOffer, gradient: 'linear-gradient(135deg,#a855f7,#7e22ce)', sub: `${inr(s.couponDiscount)} discount given`, onClick: () => goTo('coupons'), active: isActive('coupons') },
      { label: 'Coupon ROI', value: s.couponRoi === null || s.couponRoi === undefined ? '—' : `${s.couponRoi}×`, icon: MdTrendingUp, gradient: 'linear-gradient(135deg,#0f172a,#334155)', sub: `${inr(s.couponRevenue)} revenue on coupon orders` },
    ]}
  />
);

export default CampaignReport;
